/**
 * FERP Portal - Unified Auth Context
 *
 * SINGLE SOURCE OF TRUTH for authentication state
 *
 * Implements Three-Layer Authentication:
 * 1. Authentik OIDC (WebAuthn/TOTP) via @ferp/auth package
 * 2. AWS STS Token Exchange for tenant-scoped credentials
 * 3. IAM Policy Enforcement (DynamoDB LeadingKeys)
 *
 * CONSTRAINTS:
 * - Tenant ID from JWT claim ONLY (custom:tenant_id or tenant)
 * - Token storage: sessionStorage + globalThis backup
 * - NO localStorage for tokens (security requirement)
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';

// Import from @ferp/auth package for OIDC flow
import {
  getToken,
  getIdToken,
  setToken,
  configureOIDC,
  login as oidcLogin,
  logout as oidcLogout,
  maybeHandleRedirectCallback,
  startAutoRefresh,
  wasExplicitLogout,
  clearExplicitLogout,
} from '@ferp/auth';

// Import AWS STS integration
import {
  exchangeTokenForAwsCredentials,
  getCachedAwsCredentials,
  clearAwsCredentials,
  UserType,
  type AwsCredentials,
} from './aws-sts';

import type { BackendId } from '@/types/branded-ids';

// View layer enum (matches Three-Layer Interface)
export type ViewLayer = 'user' | 'admin' | 'architect';

// User roles that determine view layer
export type Role = 'user' | 'admin' | 'architect' | 'superadmin';

// Re-export AwsCredentials type
export type { AwsCredentials };

// Tenant context
export interface TenantContext {
  id: string;
  name: string;
  num: number;
  safetyLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  features: Record<string, boolean>;
}

// User context
export interface UserContext {
  id: BackendId;
  email: string;
  displayName: string;
  roles: Role[];
}

// Full auth state
export interface AuthState {
  // User identity (from Authentik OIDC)
  user: UserContext | null;

  // Tenant context (SINGLE SOURCE OF TRUTH from JWT claim)
  tenant: TenantContext | null;

  // AWS credentials (from STS token exchange)
  awsCredentials: AwsCredentials | null;

  // Session state
  isAuthenticated: boolean;
  isLoading: boolean;
  accessToken: string | null;
  idToken: string | null;

  // View layer (determined from roles)
  viewLayer: ViewLayer;

  // Error state
  error: string | null;

  // AWS STS state
  isExchangingCredentials: boolean;
  stsError: string | null;
}

// Auth context actions
interface AuthContextActions {
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  setViewLayer: (layer: ViewLayer) => void;
  exchangeAwsCredentials: () => Promise<AwsCredentials | null>;
  clearError: () => void;
}

// Combined context type
type AuthContextType = AuthState & AuthContextActions;

// Default state
const defaultAuthState: AuthState = {
  user: null,
  tenant: null,
  awsCredentials: null,
  isAuthenticated: false,
  isLoading: true,
  accessToken: null,
  idToken: null,
  viewLayer: 'user',
  error: null,
  isExchangingCredentials: false,
  stsError: null,
};

// Create context
const AuthContext = createContext<AuthContextType | null>(null);

// Import typed environment utilities
import { getEnv, getOidcConfig, getConfiguredTenant, isDevModeEnabled } from '@/utils/env';
import { DEV_ACTOR_MAP } from './dev-actors';
import { markAuthCallbackCompleted, markAuthReady } from '@/utils/apollo-client';

/**
 * Parse JWT token payload
 */
function parseJWT(token: string | null): any {
  if (!token) return null;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const base64 = (parts[1] || '')
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const padded = base64.padEnd(
      base64.length + (4 - base64.length % 4) % 4,
      '='
    );

    return JSON.parse(atob(padded));
  } catch (error) {
    console.warn('Failed to parse JWT:', error);
    return null;
  }
}

/**
 * Determine view layer from user roles
 */
function determineViewLayer(roles: Role[]): ViewLayer {
  if (roles.includes('architect') || roles.includes('superadmin')) {
    return 'architect';
  }
  if (roles.includes('admin')) {
    return 'admin';
  }
  return 'user';
}

/**
 * Map roles to UserType for AWS STS
 */
function determineUserType(roles: Role[]): UserType {
  if (roles.includes('architect') || roles.includes('superadmin')) {
    return UserType.ADMIN;
  }
  if (roles.includes('admin')) {
    return UserType.ADMIN;
  }
  // Check for healthcare indicators in roles
  if (roles.some(r => r.toString().toLowerCase().includes('healthcare'))) {
    return UserType.HEALTHCARE;
  }
  return UserType.BUSINESS;
}

/**
 * Extract tenant ID from JWT claims (SINGLE SOURCE OF TRUTH)
 *
 * In dev mode, window.__TENANT__ takes priority over JWT claims.
 * This allows developers to switch tenants via env.js without
 * reconfiguring Authentik's scope mapping.
 */
function extractTenantFromToken(payload: any): string | null {
  // Dev mode: prefer DevImpersonationPanel localStorage override, then env.js fallback
  if (isDevModeEnabled()) {
    const lsOverride = globalThis.window ? localStorage.getItem('tenant_id') : null;
    if (lsOverride) {
      console.log('🔧 Dev mode: using tenant from DevPanel override:', lsOverride);
      return lsOverride;
    }
    const configuredTenant = getConfiguredTenant();
    if (configuredTenant) {
      console.log('🔧 Dev mode: using tenant from env.js:', configuredTenant);
      return configuredTenant;
    }
  }

  // Try standard claim locations in order of preference
  const tokenTenant =
    payload?.['custom:tenant_id'] ||
    payload?.['tenant_id'] ||
    payload?.tenant ||
    payload?.['ak_proxy']?.['user_attributes']?.['tenant'] ||
    null;

  // Fallback to configured tenant from env.js if token doesn't have tenant
  if (!tokenTenant) {
    const configuredTenant = getConfiguredTenant();
    if (configuredTenant) {
      console.log('📋 Using configured tenant from env.js:', configuredTenant);
      return configuredTenant;
    }
  }

  return tokenTenant;
}

/**
 * Extract tenant_num (integer) from JWT claims or dev config.
 *
 * DRR-156: Needed for recommendations query (tenantNum: Int!).
 * In dev mode, reads window.__TENANT_NUM__ from env.js.
 * In prod, reads from JWT custom:tenant_num claim.
 */
function extractTenantNumFromToken(payload: any): number {
  // Dev mode: prefer env.js config
  if (isDevModeEnabled()) {
    const envNum = globalThis.window?.__TENANT_NUM__;
    if (typeof envNum === 'number') return envNum;
  }

  // JWT claim - check all possible claim names
  // ferp_tenant_num: Authentik custom claim (production)
  // custom:tenant_num: Cognito format
  // tenant_num: generic OIDC
  const num =
    payload?.['ferp_tenant_num'] ??
    payload?.['custom:tenant_num'] ??
    payload?.['tenant_num'] ??
    0;

  return typeof num === 'number' ? num : parseInt(num, 10) || 0;
}

/**
 * Extract roles from JWT claims
 *
 * Priority order (DRR-073):
 * 1. ferp_roles — emitted by Authentik scope mapping from group membership
 * 2. groups — Authentik group names (ferp-admin, ferp-architects, etc.)
 * 3. roles — generic OIDC roles claim
 */
function extractRolesFromToken(payload: any): Role[] {
  // Collect roles from ALL sources:
  // 1. ferp_roles (FERP role IDs like "role-4-0-12")
  // 2. groups (Authentik groups like "hillside-admin", "ferp-architects")
  // 3. roles (generic OIDC roles claim)
  //
  // Check ALL of them, not just the first non-empty source.
  // A user in "hillside-admin" group should get 'admin' role even if
  // ferp_roles only contains FERP role IDs like "role-4-0-12".
  const ferpRoles: string[] = payload?.ferp_roles ?? [];
  const groups: string[] = payload?.groups ?? [];
  const genericRoles: string[] = payload?.roles ?? [];

  const allRoleSources = [...ferpRoles, ...groups, ...genericRoles];

  let hasArchitect = false;
  let hasAdmin = false;

  for (const r of allRoleSources) {
    const lower = r.toLowerCase();
    // Exact matches for FERP role types
    if (lower === 'architect' || lower === 'superadmin') {
      hasArchitect = true;
    }
    if (lower === 'admin') {
      hasAdmin = true;
    }
    // Partial matches for group names (hillside-admin, ferp-architects)
    if (lower.includes('architect') || lower.includes('superadmin')) {
      hasArchitect = true;
    }
    if (lower.includes('admin')) {
      hasAdmin = true;
    }
  }

  // Return highest privilege found
  if (hasArchitect) return ['architect'];
  if (hasAdmin) return ['admin'];
  return ['user'];
}

/**
 * Configure OIDC from environment
 */
function initializeOIDC(): boolean {
  const { issuer, clientId, redirectUri, scopes } = getOidcConfig();
  const scope = scopes || 'openid profile email';

  if (issuer && clientId && redirectUri) {
    configureOIDC({
      issuer,
      clientId,
      redirectUri,
      scope,
      useRefreshToken: true,
    });
    if (isDevModeEnabled()) console.log('🔐 OIDC configured:', { issuer, clientId, redirectUri });
    return true;
  }

  console.warn('⚠️ OIDC not fully configured');
  return false;
}

/**
 * Auth Provider Component
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(defaultAuthState);
  const initRef = useRef(false);

  /**
   * Process token and extract user/tenant info
   */
  const processToken = useCallback((accessToken: string, idToken?: string | null) => {
    // Prefer id_token for claims extraction — Authentik scope mappings
    // (ferp_roles, tenant, etc.) are guaranteed in the id_token via
    // include_claims_in_id_token: true. Fall back to access_token.
    const payload = parseJWT(idToken || accessToken);
    if (!payload) {
      return { success: false, error: 'Invalid token' };
    }

    // Check token expiration
    if (payload.exp && payload.exp < Math.floor(Date.now() / 1000)) {
      return { success: false, error: 'Token expired' };
    }

    // Extract user info
    const roles = extractRolesFromToken(payload);
    const user: UserContext = {
      id: payload.sub as BackendId,
      email: payload.email || 'unknown',
      displayName: payload.name || payload.preferred_username || payload.email || 'User',
      roles,
    };

    // Extract tenant from token (SINGLE SOURCE OF TRUTH)
    const tenantId = extractTenantFromToken(payload);
    // Extract tenant_num from JWT claim or dev config (DRR-156: needed for recommendations query)
    const tenantNum = extractTenantNumFromToken(payload);
    const tenant: TenantContext | null = tenantId
      ? {
          id: tenantId,
          name: tenantId, // Will be fetched from backend
          num: tenantNum,
          safetyLevel: 'MEDIUM', // Default, will be fetched
          features: {},
        }
      : null;

    const jwtViewLayer = determineViewLayer(roles);
    // DEBUG: Track JWT-derived view layer
    console.log('[FERP-VIEWLAYER-DEBUG] processToken:', {
      email: user.email,
      roles,
      jwtViewLayer,
      payload_groups: payload?.groups,
      payload_ferp_roles: payload?.ferp_roles,
    });

    return {
      success: true,
      user,
      tenant,
      viewLayer: jwtViewLayer,
    };
  }, []);

  /**
   * Handle OIDC callback
   */
  const handleCallback = useCallback(async (): Promise<boolean> => {
    try {
      const handled = await maybeHandleRedirectCallback();
      if (handled) {
        if (isDevModeEnabled()) console.log('✅ OIDC callback handled successfully');
        // Signal to Apollo that callback completed - it should wait for tokens
        markAuthCallbackCompleted();
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ OIDC callback error:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Callback failed',
      }));
      return false;
    }
  }, []);

  /**
   * Check session and initialize auth state
   */
  const checkSession = useCallback(async () => {
    const env = getEnv();

    // Check for mock auth in development
    if (env.ENABLE_MOCK_AUTH === 'true') {
      // Clear any stale OIDC tokens from previous real auth sessions.
      // These interfere with mock auth: getAuthToken() in apollo-client
      // and @ferp/api-client will pick them up and send the real JWT,
      // which the gateway stub mode rejects with 401, triggering a
      // redirect to /login before the user sees any data.
      try {
        sessionStorage.removeItem('auth:token');
        sessionStorage.removeItem('auth:id_token');
      } catch { /* storage access may fail */ }

      // Use 'dev' token which the gateway stub mode accepts
      // Respect localStorage overrides from DevTool panels
      const mockTenantId = localStorage.getItem('tenant_id') || getConfiguredTenant() || 'ferp-system';

      // Actor identity from DevImpersonationPanel (Ctrl+D)
      // DEV_ACTOR_MAP shared with ceremony forms (auth/dev-actors.ts)
      const mockActorAuthId = localStorage.getItem('dev_actor_auth_id') || '2_1';
      const mockActor = DEV_ACTOR_MAP[mockActorAuthId] ?? DEV_ACTOR_MAP['2_1']!;
      console.log(`📱 Mock auth: ${mockActor.name} (${mockActorAuthId}) @ ${mockTenantId}`);
      // Clear saved viewLayer when actor changes so the new actor's role determines the layer
      const savedLayerActor = sessionStorage.getItem('portal:viewLayer:actor');
      if (savedLayerActor && savedLayerActor !== mockActorAuthId) {
        sessionStorage.removeItem('portal:viewLayer');
      }
      sessionStorage.setItem('portal:viewLayer:actor', mockActorAuthId);

      // Role-scoped access: back-office gets full access, frontline gets user-only
      const ROLE_TO_ROLES: Record<string, Role[]> = {
        ADMIN: ['architect', 'admin', 'user'],
        DON: ['user'],
        CLAIMS_REVIEWER: ['admin', 'user'],
        FINANCIAL_ANALYST: ['admin', 'user'],
        QA_SPECIALIST: ['admin', 'user'],
        CARE_COORDINATOR: ['admin', 'user'],
        LPN: ['admin', 'user'],
        BILLING_CLERK: ['admin', 'user'],
        PAYROLL_PROCESSOR: ['admin', 'user'],
        PAYROLL_MANAGER: ['admin', 'user'],
        ACCOUNTANT: ['admin', 'user'],
        AUDITOR: ['admin', 'user'],
        MED_AIDE: ['user'],
        HOME_CARE_WORKER: ['user'],
        RESIDENT_ASSOCIATE: ['user'],
        EMPLOYEE: ['user'],
        RESIDENT: ['user'],
        VOLUNTEER: ['user'],
      };
      const mockRoles = ROLE_TO_ROLES[mockActor.role] ?? ['user'];
      let mockViewLayer: 'architect' | 'admin' | 'user' = 'user';
      // Restore DevImpersonationPanel viewLayer override (persists across reloads)
      const savedLayer = sessionStorage.getItem('portal:viewLayer');
      if (savedLayer) {
        mockViewLayer = savedLayer.toLowerCase() as 'architect' | 'admin' | 'user';
      } else if (mockRoles.includes('architect')) {
        mockViewLayer = 'architect';
      } else if (mockRoles.includes('admin')) {
        mockViewLayer = 'admin';
      }

      setState({
        user: {
          id: mockActorAuthId as BackendId,
          email: mockActor.email,
          displayName: mockActor.name,
          roles: mockRoles,
        },
        tenant: {
          id: mockTenantId,
          name: mockTenantId,
          num: globalThis.window?.__TENANT_NUM__ ?? 2,
          safetyLevel: 'LOW',
          features: { devMode: true },
        },
        awsCredentials: null,
        isAuthenticated: true,
        isLoading: false,
        accessToken: 'dev',
        idToken: null,
        viewLayer: mockViewLayer,
        error: null,
        isExchangingCredentials: false,
        stsError: null,
      });
      // Signal auth ready for mock mode (Apollo doesn't wait but keeps state consistent)
      markAuthReady();
      return;
    }

    // Initialize OIDC configuration
    initializeOIDC();

    // Handle OIDC callback if present
    const callbackHandled = await handleCallback();
    if (callbackHandled) {
      // Callback was handled, tokens should now be in storage
      // Re-check after a small delay to allow storage to update
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Get tokens from @ferp/auth
    const accessToken = getToken();
    const idToken = getIdToken();

    if (!accessToken) {
      if (isDevModeEnabled()) console.log('📝 No access token found');
      setState({ ...defaultAuthState, isLoading: false });
      return;
    }

    // Process the token
    const result = processToken(accessToken, idToken);
    if (!result.success) {
      console.warn('⚠️ Token processing failed:', result.error);
      // Clear invalid tokens
      setToken(undefined);
      sessionStorage.removeItem('auth:id_token');
      setState({ ...defaultAuthState, isLoading: false, error: result.error ?? null });
      return;
    }

    if (isDevModeEnabled()) {
      console.log('✅ Session restored:', {
        user: result.user?.email,
        tenant: result.tenant?.id,
        viewLayer: result.viewLayer,
      });
    }

    // Start token auto-refresh
    startAutoRefresh();

    // Check for cached AWS credentials
    const cachedCredentials = getCachedAwsCredentials();

    // Dev mode: restore DevImpersonationPanel viewLayer override from sessionStorage
    // (saved by setViewLayer). Without this, page reloads reset viewLayer to JWT roles.
    let effectiveViewLayer = result.viewLayer!;
    if (isDevModeEnabled()) {
      const savedLayer = sessionStorage.getItem('portal:viewLayer');
      if (savedLayer) {
        effectiveViewLayer = savedLayer.toLowerCase() as ViewLayer;
      }
    }

    // Signal to Apollo that auth is ready - tokens are confirmed valid
    markAuthReady();

    setState({
      user: result.user!,
      tenant: result.tenant ?? null,
      awsCredentials: cachedCredentials,
      isAuthenticated: true,
      isLoading: false,
      accessToken,
      idToken: idToken || null,
      viewLayer: effectiveViewLayer,
      error: null,
      isExchangingCredentials: false,
      stsError: null,
    });
  }, [handleCallback, processToken]);

  // Initialize auth on mount (guarded to prevent double-init in StrictMode)
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    checkSession();
  }, [checkSession]);

  // Listen for token changes from @ferp/auth and DevImpersonationPanel persona switches.
  // Separate from init guard so the listener survives React 18 StrictMode remount.
  useEffect(() => {
    const handleTokenSet = () => {
      if (isDevModeEnabled()) console.log('🔄 Token updated, re-checking session');
      checkSession();
    };

    window.addEventListener('ui:token-set', handleTokenSet);

    return () => {
      window.removeEventListener('ui:token-set', handleTokenSet);
    };
  }, [checkSession]);

  // Login action - redirect to OIDC provider
  const login = useCallback(async () => {
    try {
      // Clear explicit logout flag
      clearExplicitLogout();

      // Initialize OIDC if not done
      initializeOIDC();

      // Start OIDC login flow
      await oidcLogin();
    } catch (error) {
      console.error('Login failed:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Login failed',
      }));
    }
  }, []);

  // Logout action
  const logout = useCallback(async () => {
    try {
      // Clear AWS credentials
      clearAwsCredentials();

      // Clear state first
      setState({ ...defaultAuthState, isLoading: false });

      // Perform OIDC logout (may redirect)
      await oidcLogout();
    } catch (error) {
      console.error('Logout error:', error);
      // Even if OIDC logout fails, clear local state
      setState({ ...defaultAuthState, isLoading: false });
    }
  }, []);

  // Refresh token action
  const refreshToken = useCallback(async () => {
    // @ferp/auth handles token refresh automatically via startAutoRefresh
    // This is exposed for manual refresh if needed
    if (isDevModeEnabled()) console.log('🔄 Manual token refresh requested');
    await checkSession();
  }, [checkSession]);

  // Exchange tokens for AWS credentials
  const exchangeAwsCredentials = useCallback(async (): Promise<AwsCredentials | null> => {
    const idToken = getIdToken();
    if (!idToken) {
      console.warn('⚠️ No ID token available for AWS credential exchange');
      setState(prev => ({ ...prev, stsError: 'No ID token available' }));
      return null;
    }

    setState(prev => ({ ...prev, isExchangingCredentials: true, stsError: null }));

    try {
      const userType = state.user ? determineUserType(state.user.roles) : UserType.BUSINESS;
      const credentials = await exchangeTokenForAwsCredentials(idToken, userType);

      setState(prev => ({
        ...prev,
        awsCredentials: credentials,
        isExchangingCredentials: false,
        stsError: null,
      }));

      if (isDevModeEnabled()) console.log('✅ AWS credentials obtained');
      return credentials;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'AWS credential exchange failed';
      console.error('❌ AWS credential exchange failed:', errorMsg);

      setState(prev => ({
        ...prev,
        isExchangingCredentials: false,
        stsError: errorMsg,
      }));

      return null;
    }
  }, [state.user]);

  // Set view layer
  const setViewLayer = useCallback((layer: ViewLayer) => {
    setState((prev) => ({ ...prev, viewLayer: layer }));
    sessionStorage.setItem('portal:viewLayer', layer.toUpperCase());
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null, stsError: null }));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        refreshToken,
        setViewLayer,
        exchangeAwsCredentials,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access auth context
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook to get tenant ID (SINGLE SOURCE OF TRUTH)
 *
 * DRR-259: Returns NUMERIC tenant ID as string (e.g., "2" not "hillside-harmony").
 * Backend repositories now require numeric tenant IDs for key format.
 */
export function useTenantId(): string | null {
  const { tenant } = useAuth();
  // DRR-259: Return numeric ID, fall back to slug for backwards compat
  if (tenant?.num && tenant.num > 0) {
    return String(tenant.num);
  }
  return tenant?.id ?? null;
}

/**
 * Hook to get tenant slug (for display purposes only)
 *
 * Use this when you need the human-readable slug (e.g., "hillside-harmony")
 * for display, URLs, or branding. For API calls, use useTenantId() which
 * returns the numeric ID per DRR-259.
 */
export function useTenantSlug(): string | null {
  const { tenant } = useAuth();
  return tenant?.id ?? null;
}

/**
 * Hook to get current view layer
 */
export function useViewLayer(): ViewLayer {
  const { viewLayer } = useAuth();
  return viewLayer;
}

/**
 * Hook to check if user has specific role
 */
export function useHasRole(role: Role): boolean {
  const { user } = useAuth();
  return user?.roles.includes(role) ?? false;
}

/**
 * Hook to get AWS credentials (with automatic exchange if needed)
 */
export function useAwsCredentials(): {
  credentials: AwsCredentials | null;
  isLoading: boolean;
  error: string | null;
  exchange: () => Promise<AwsCredentials | null>;
} {
  const { awsCredentials, isExchangingCredentials, stsError, exchangeAwsCredentials } = useAuth();

  return {
    credentials: awsCredentials,
    isLoading: isExchangingCredentials,
    error: stsError,
    exchange: exchangeAwsCredentials,
  };
}
